import { useEffect, useRef, useState } from 'react'
import { useLang } from '../i18n'
import type { Lang } from '../i18n'
import { FAQ_DATA } from '../data/faqData'
import type { FaqEntry } from '../data/faqData'

function matchesQuery(entry: FaqEntry, query: string, lang: Lang): boolean {
  if (!query.trim()) return true
  const q = query.toLowerCase()
  const question = (lang === 'zh' ? entry.q_zh : entry.q_en).toLowerCase()
  const answer = (lang === 'zh' ? entry.a_zh : entry.a_en).toLowerCase()
  const kw = entry.keywords.join(' ').toLowerCase()
  return question.includes(q) || answer.includes(q) || kw.includes(q)
}

export function HelpPanel() {
  const { t, lang } = useLang()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // When panel opens, compute fixed position from button's viewport rect
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPanelPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      })
    }
    if (!open) {
      setQuery('')
      setExpandedId(null)
      setPanelPos(null)
    }
  }, [open])

  const filtered = FAQ_DATA.filter((entry) => matchesQuery(entry, query, lang))

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: panelPos?.top ?? 0,
    right: panelPos?.right ?? 0,
    width: '380px',
    maxHeight: '520px',
    overflowY: 'auto',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    zIndex: 1000,
    border: '1px solid var(--border)',
  }

  const headerStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderBottom: '1px solid var(--border)',
    fontWeight: 600,
    fontSize: '14px',
    color: 'var(--text-primary)',
    flexShrink: 0,
  }

  const searchStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    fontSize: '13px',
    background: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        aria-label={t('helpLabel')}
        style={{
          padding: '4px 10px',
          borderRadius: '4px',
          border: '1px solid rgba(255,255,255,0.4)',
          background: open ? 'rgba(255,255,255,0.2)' : 'transparent',
          color: 'var(--nav-text)',
          cursor: 'pointer',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        }}
      >
        {t('helpLabel')}
      </button>

      {open && (
        <div style={panelStyle}>
          <div style={headerStyle}>{t('helpPanelTitle')}</div>

          {/* Search box */}
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setExpandedId(null)
              }}
              placeholder={t('helpSearchPlaceholder')}
              style={searchStyle}
            />
          </div>

          {/* FAQ list */}
          {filtered.length === 0 ? (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: '13px',
              }}
            >
              {t('helpNoMatch')}
            </div>
          ) : (
            filtered.map((entry) => {
              const isExpanded = expandedId === entry.id
              const question = lang === 'zh' ? entry.q_zh : entry.q_en
              const answer = lang === 'zh' ? entry.a_zh : entry.a_en
              return (
                <div
                  key={entry.id}
                  style={{
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '8px',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 500, flex: 1 }}>
                      {entry.id}. {question}
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-muted)',
                        flexShrink: 0,
                        lineHeight: '20px',
                      }}
                    >
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  </button>
                  {isExpanded && (
                    <div
                      style={{
                        padding: '4px 16px 14px',
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                        lineHeight: '1.6',
                        background: 'var(--bg-elevated)',
                      }}
                    >
                      {answer}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
